import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Badge, Loading, ErrorState } from '../../components/ui.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { usePermissions } from '../../auth/session.tsx';
import { useScreens } from '../../api/screens.ts';
import { guessTimezone } from '../../lib/tz.ts';
import { ApiError } from '../../api/client.ts';
import {
  useReportSchedules,
  useUpdateReportSchedule,
  useDeleteReportSchedule,
  type ReportSchedule,
} from '../../api/reports.ts';
import { ReportScheduleDrawer, WEEKDAYS } from './ReportScheduleDrawer.tsx';

const METRIC_LABEL: Record<string, string> = {
  uptime: 'Uptime',
  playback: 'Playback',
  top_media: 'Top content',
};

/** "Weekly on Monday at 09:00" / "Daily at 09:00" (in the report's timezone). */
function scheduleSummary(r: ReportSchedule): string {
  const at = `${String(r.hour).padStart(2, '0')}:00`;
  if (r.freq === 'weekly') {
    const day = r.day_of_week != null ? WEEKDAYS[r.day_of_week] : 'a weekday';
    return `Weekly on ${day} at ${at}`;
  }
  return `Daily at ${at}`;
}

function scopeLabel(r: ReportSchedule): string {
  if (r.scope_type === 'org') return 'Whole org';
  return r.scope_name ?? '(deleted)';
}

export function ReportSchedulesSection() {
  const perms = usePermissions();
  const reports = useReportSchedules();
  const update = useUpdateReportSchedule();
  const del = useDeleteReportSchedule();
  const screens = useScreens();
  const [drawer, setDrawer] = useState<null | { mode: 'create' } | { mode: 'edit'; report: ReportSchedule }>(null);
  const [confirmDelete, setConfirmDelete] = useState<ReportSchedule | null>(null);

  // Default new reports to the fleet's most common screen timezone.
  const defaultTz = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of screens.data ?? []) counts.set(s.timezone, (counts.get(s.timezone) ?? 0) + 1);
    let best: string | null = null;
    let bestN = 0;
    for (const [tz, n] of counts) if (n > bestN) { best = tz; bestN = n; }
    return best ?? guessTimezone();
  }, [screens.data]);

  function toggle(r: ReportSchedule) {
    update.mutate(
      { id: r.id, body: { enabled: !r.enabled } },
      { onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed') },
    );
  }

  return (
    <div className="card card-pad">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="card-title" style={{ marginBottom: 2 }}>Scheduled reports</div>
          <p className="text-dim" style={{ fontSize: 'var(--fs-sm)' }}>
            Periodic uptime and playback digests, delivered to Slack.
          </p>
        </div>
        {perms.canManageContent && (
          <Button variant="primary" onClick={() => setDrawer({ mode: 'create' })}>
            New report
          </Button>
        )}
      </div>

      <div className="hint-box info mt-2">
        Reports are delivered via Slack — link your workspace in the Slack section above if you
        haven&apos;t yet.
      </div>

      {reports.isLoading ? (
        <div className="mt-4"><Loading /></div>
      ) : reports.isError ? (
        <div className="mt-4"><ErrorState message="Could not load scheduled reports." onRetry={() => reports.refetch()} /></div>
      ) : reports.data!.length === 0 ? (
        <p className="text-dim mt-4">No scheduled reports yet.</p>
      ) : (
        <div className="table-wrap cards mt-4">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Scope</th>
                <th>Schedule</th>
                <th>Includes</th>
                <th>Delivery</th>
                <th>Enabled</th>
                {perms.canManageContent && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {reports.data!.map((r) => (
                <tr key={r.id}>
                  <td className="cell-strong">{r.name}</td>
                  <td data-label="Scope">{scopeLabel(r)}</td>
                  <td data-label="Schedule" className="cell-dim">{scheduleSummary(r)}</td>
                  <td data-label="Includes">
                    <div className="chip-row">
                      {r.metrics.map((m) => (
                        <Badge key={m} tone="neutral">{METRIC_LABEL[m] ?? m}</Badge>
                      ))}
                    </div>
                  </td>
                  <td data-label="Delivery" className="cell-dim">
                    {r.slack_target ? `Channel ${r.slack_target}` : 'Direct message'}
                  </td>
                  <td data-label="Enabled">
                    <label className="a8-switch">
                      <input
                        type="checkbox"
                        checked={r.enabled}
                        disabled={!perms.canManageContent || update.isPending}
                        onChange={() => toggle(r)}
                        aria-label={`${r.enabled ? 'Disable' : 'Enable'} ${r.name}`}
                      />
                      <span className="a8-switch-track" aria-hidden />
                    </label>
                  </td>
                  {perms.canManageContent && (
                    <td data-label="">
                      <div className="row" style={{ gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
                        <Button size="sm" onClick={() => setDrawer({ mode: 'edit', report: r })}>Edit</Button>
                        <Button size="sm" variant="danger" onClick={() => setConfirmDelete(r)}>Delete</Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawer && (
        <ReportScheduleDrawer
          initial={drawer.mode === 'edit' ? drawer.report : undefined}
          defaultTz={defaultTz}
          onClose={() => setDrawer(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete scheduled report?"
          message={`“${confirmDelete.name}” will stop being delivered. This can’t be undone.`}
          confirmLabel="Delete"
          danger
          busy={del.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            del.mutate(confirmDelete.id, {
              onSuccess: () => { toast.success('Report deleted'); setConfirmDelete(null); },
              onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
            })
          }
        />
      )}
    </div>
  );
}
